import { checkText } from 'smile2emoji';
import * as utils from '../../utils/utils.js';
import * as userPerms from '../../utils/userPerms.js';

export default class Chat {
    constructor($scope, $timeout, $window, watchService, uiService) {
        this.$timeout = $timeout;
        this.service = uiService;
        this.watchService = watchService;
        this.messages = [];
        this.active = false;
        this.isScrolling = false;
        this.postAsAnnouncement = false;
        this.signalRStatus = this.service.SIGNALR_STATUS.CONNECTED;
        this.numMessagesShown = 0;
        this.localSignInUrl = utils.getSignInUrl(false, null, this.watchService.event.accessKey);
        this.imageVer = siteSettings.imageVersion;
        this.siteSettings = siteSettings;
        this.user = utils.getCurrentUser();
        this.canSeeEventAdminLinks = userPerms.allowedToSeeEventAdminLinks(
            this.user,
            this.watchService.eAdms
        );

        console.log('Chat started');
        this.service.start();

        // Selectors
        this.chatMessagesSelector = '.vl-Chat-messages-container';

        /**
         * Sets the context ID; used for identifying the current user.
         * Event is triggered backend.
         */
        this.service.on('updateConnectionId', id => {
            this.connectionID = id;
        });

        /**
         * The message is broadcasted from the backend
         * to all clients. This updates the UI with the message.
         * Event is triggered backend.
         */
        this.service.on(
            'broadcastMessage',
            ({ displayName, message, userId, connectionId, timeStamp, userEmail }) => {
                // If the previous message is posted by the same user it must be reformatted.
                // NB! This mutates the messages array.
                let lastMessage;
                if (this.messages.length > 0) {
                    lastMessage = this.messages[this.messages.length - 1];
                    lastMessage.hasFollower =
                        lastMessage.connectionId === connectionId &&
                        lastMessage.nick === displayName;
                }

                // Create a comparator. Not required (nor possible) for fullscreen chat mode.
                let id;
                if (!this.fullscreen && this.watchService.user !== null) {
                    ({
                        user: { id }
                    } = this.watchService);
                }

                const comparator = {
                    identifier: id,
                    id: userId
                };

                // Append the new message to messages
                this.messages = [
                    ...this.messages,
                    this.chatMessageFactory(
                        {
                            displayName,
                            message: checkText(message),
                            timeStamp,
                            userId,
                            userEmail
                        },
                        {
                            mine: comparator.identifier === comparator.id,
                            time: utils.calculateDistanceFromTime(new Date()),
                            connectionId,
                            isFollower: lastMessage && lastMessage.hasFollower
                        }
                    )
                ];

                let date = new Date(Date.now() + 1000);
                localStorage.setItem('newComment', date);

                this.signalNewComment();
            }
        );

        /**
         * The initial messages are broadcasted from the backend
         * to a new client. This updates the UI with all previous messages.
         * Event is triggered backend.
         */

        /* eslint-disable no-unused-vars */
        this.service.on(
            'broadcastInitialViewer',
            ({ chatMessages, userQuestions, userAnswers, announcements }) => {
                /* eslint-enable */
                const announcement = announcements.map(this.announcementFactory);
                const chatMessagesArr = chatMessages.map(this.chatMessageFactory.bind(this));

                // Combine message types and sort
                const messagesHistory = [...chatMessagesArr, ...announcement].sort(
                    (a, b) => new Date(a.timeStamp) - new Date(b.timeStamp)
                );

                // Modify chat messages based on follower status
                this.messages = messagesHistory.map((item, index, messagesHistory) => {
                    if (!item.isAnnouncement) {
                        item.isFollower =
                            index > 0 && messagesHistory[index - 1].nick === item.nick;
                        item.hasFollower =
                            index !== messagesHistory.length - 1 &&
                            messagesHistory[index + 1].nick === item.nick;
                    }
                    return item;
                });
                this.handleScrollEvent();
                this.watchService.userInteractionResponsive = true;
                if (announcements.length > 0) this.signalNewAnnouncement();
            }
        );

        /**
         * Display announcement in chat
         */
        this.service.on('postAnnouncement', annoucement => {
            let currAnnouncement = this.announcementFactory(annoucement);
            this.messages = [...this.messages, currAnnouncement];

            let date = new Date(Date.now() + 1000);
            localStorage.setItem('newComment', date);

            this.watchService.showFullscreenAnnouncement(
                currAnnouncement.nick,
                currAnnouncement.text
            );
            this.signalNewAnnouncement();
        });

        this.handleWindowResizeEvent();
    }

    $onInit() {
        this.numMessagesShown = this.pageSize;
        this.morePlacehoderText =
            this.isAnnouncements === 'yes' ? 'More Announcements' : 'More Comments';
    }

    changeNameEvent() {
        window.location.href = this.localSignInUrl;
    }
    /**
     * Prevent message window from scrolling on new messages
     * if the user is currently scrolling
     */
    handleScrollEvent() {
        const el = document.querySelector(this.chatMessagesSelector);
        angular.element(el).on('scroll', e => {
            this.isScrolling =
                e.target.scrollHeight - e.target.scrollTop - e.target.offsetHeight >= 10;
        });
    }
    /**
     * Scrolls the chat to bottom on resize
     */
    handleWindowResizeEvent() {
        window.addEventListener('resize', () => {
            this.service.isScrolling = false;
        });
    }

    /**
     * Enables the messages input box and sets a placeholder message.
     */
    postingAllowed() {
        let userAuthenticated = this.watchService.canUserComment();
        if (userAuthenticated) {
            this.placeholder =
                this.isAnnouncements === 'yes' || this.postAsAnnouncement
                    ? this.siteSettings.texts['Webcast_PostAnnouncementOption']
                    : this.siteSettings.texts['Webcast_PostCommentOption'];
            return this.canSeeEventAdminLinks || this.isAnnouncements !== 'yes';
        }
        return false;
    }

    /**
     * The message is sent to the backend, which broadcasts
     * it to all clients.
     */
    sendMessage() {
        if (!this.message) {
            return;
        }
        if (!this.message.text) {
            return;
        }
        this.isScrolling = false;
        const fnToInvoke =
            this.isAnnouncements === 'yes' || this.postAsAnnouncement
                ? 'postAnnouncement'
                : 'sendComment';
        this.service.invoke(fnToInvoke, [this.message.text.replace(/<[^>]*>/g, '')]);
        this.message.text = '';
    }

    showMore() {
        this.numMessagesShown += this.pageSize;
    }

    /**
     * Returns a chat message
     */
    chatMessageFactory({ displayName, message, timeStamp, userId, userEmail }, newMessage = {}) {
        // Fullscren does not require user to be identified, nor is it available.
        let id;
        if (!this.fullscreen && this.watchService.user !== null) {
            ({
                user: { id }
            } = this.watchService);
        }

        return Object.assign(
            {},
            {
                nick: displayName,
                cdsid:
                    userEmail.toLowerCase().indexOf('@volvocars.com') > 0
                        ? userEmail.toLowerCase().replace(/@volvocars.com/, '')
                        : 'anonymous',
                text: checkText(utils.replaceNewline(message, '<br />')),
                mine: id === userId,
                time: utils.calculateDistanceFromTime(timeStamp),
                timeStamp
            },
            newMessage
        );
    }

    /**
     * Returns an annoucement
     */
    announcementFactory({ timeStamp, displayName, message, userName }) {
        return {
            nick: displayName,
            cdsid:
                userName.toLowerCase().indexOf('@volvocars.com') > 0
                    ? userName.toLowerCase().replace(/@volvocars.com/, '')
                    : 'anonymous',
            text: checkText(utils.replaceNewline(message, '<br />')),
            isAnnouncement: true,
            time: utils.calculateDistanceFromTime(timeStamp),
            timeStamp
        };
    }

    isConnected() {
        return this.service.connectionStatus === this.service.SIGNALR_STATUS.CONNECTED;
    }

    isConnecting() {
        return this.service.connectionStatus === this.service.SIGNALR_STATUS.CONNECTING;
    }
}

Chat.$inject = ['$scope', '$timeout', '$window', 'watchService', 'uiService'];
